// @ts-nocheck
import React, { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { SheetColumn, SheetScope, writeCell } from "@/lib/liveSheets";

const fmtCurrency = (n: any) =>
  n == null || n === ""
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n));
const fmtDate = (s: any) => (s ? format(new Date(s), "MMM d, yyyy") : "—");
const fmtDateTime = (s: any) => (s ? format(new Date(s), "MMM d, yyyy h:mm a") : "—");

interface Props {
  row: any;
  col: SheetColumn;
  scope: SheetScope;
  token: string;
  permission: "view" | "comment" | "edit";
  onUpdate: (rowId: string, key: string, value: any) => void;
  idField: string;
}

export default function EditableCell({ row, col, scope, token, permission, onUpdate, idField }: Props) {
  const value = row[col.key];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const rowId = row[idField];

  const display = () => {
    switch (col.type) {
      case "currency":
        return fmtCurrency(value);
      case "date":
        return fmtDate(value);
      case "datetime":
        return fmtDateTime(value);
      case "bool":
        return value ? "Yes" : "No";
      case "select": {
        const opt = (col.options || []).find((o) => o.value === value);
        return opt?.label || value || "—";
      }
      default:
        return value == null || value === "" ? "—" : String(value);
    }
  };

  const canEdit = permission === "edit" && col.editable && col.field;

  const save = async (newValue: any) => {
    setEditing(false);
    if (String(newValue ?? "") === String(value ?? "")) return;
    onUpdate(rowId, col.key, newValue); // optimistic
    try {
      await writeCell(scope, token, rowId, col.field, newValue);
    } catch (e: any) {
      onUpdate(rowId, col.key, value); // revert
      toast.error(e.message || "Failed to save");
    }
  };

  if (!canEdit) {
    return <span className="text-sm">{display()}</span>;
  }

  // boolean toggles inline
  if (col.type === "bool") {
    return (
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => save(e.target.checked)}
        className="h-4 w-4 cursor-pointer accent-[#002E62]"
      />
    );
  }

  if (col.type === "select") {
    return (
      <select
        value={value ?? ""}
        onChange={(e) => save(e.target.value)}
        className="h-8 rounded border border-input bg-background px-1 text-sm"
      >
        <option value="">—</option>
        {(col.options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  if (editing) {
    return (
      <input
        autoFocus
        type={col.type === "currency" || col.type === "number" ? "number" : "text"}
        step="0.01"
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => save(draft)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save(draft);
          if (e.key === "Escape") setEditing(false);
        }}
        className="h-8 w-full rounded border border-[#BC8D09] bg-white px-1 text-sm"
      />
    );
  }

  return (
    <span
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className="block min-h-[1.5rem] cursor-text rounded px-1 text-sm hover:bg-amber-50"
    >
      {display()}
    </span>
  );
}
